import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import { getAllEvents, deselectEvent, getEventSharedWithMe, editEventSharedWith } from "../../redux/actions/events";
import { getUser, postUser } from "../../redux/actions/users";
import { useTranslation } from "react-i18next";
import Modal from "../../components/generic/modal";
import { formatBalance, formatCount, formatShortDate } from "../../helpers/functions";
import StatsService from "../../services/statsService";
import { cx, RevButton, RevChip, RevEmpty, RevFigure, RevPageHead, RevSegmented, RevSkeleton } from "../../components/ds";
import { matchesEventsSearch, readEventsSearch } from "../../helpers/eventsSearch";
import { isEventPublished, isPublicGalleryOn } from "../../helpers/eventPublication";

// Las clases de la rejilla, la superficie y la barra de filtros son de esta pantalla: el
// esqueleto de la carga de datos y el del arranque de la app pintan la MISMA lista, así
// que el literal vive una sola vez.
const clasesEsqueleto = (gridClassName) => ({
    metricas: 'rev-events-metrics',
    filtros: 'rev-events-toolbar',
    contador: 'rev-events-toolbar__count',
    superficie: 'rev-events-surface',
    cabecera: `${gridClassName} rev-events-grid__head`,
    fila: `${gridClassName} rev-events-row rev-events-row--inerte`,
});

// Hueco del listado mientras PrivateRoute resuelve la autenticación (routerConfig lo pinta
// dentro del cromo). Misma forma que pinta la lista cargada —4 métricas (la de saldo sin
// línea de variación) y las 7 columnas de `.rev-events-grid`—, porque ya no hay columna
// condicional. Sin `etiquetas` ni `contador`: todavía no hay ni usuario ni eventos con los
// que rotularlos. El botón es un molde: mismo alto, ningún control vivo antes de autenticar.
export const EsqueletoEventos = () => {
    const { t } = useTranslation();
    return (
        <div className="rev-pagina">
            <RevPageHead
                title={t('events.title')}
                subtitle={t('eventsList.subtitle')}
                actions={<RevSkeleton.Boton>{t('events.newEventButton.text')}</RevSkeleton.Boton>}
            />
            <RevSkeleton.Page
                forma="lista"
                label={t('event.photos.loading')}
                metricas={4}
                sinDelta={1}
                columnas={7}
                filas={5}
                clases={clasesEsqueleto('rev-events-grid')}
            />
        </div>
    );
};

const EventsList = ({ ...props }) => {
    const { t } = useTranslation();
    const [showNewsModal, setShowNewsModal] = useState(false);
    const [showDenyModal, setShowDenyModal] = useState(false);
    const [eventIdToDeny, setEventIdToDeny] = useState(undefined);
    const [shareLoading, setShareLoading] = useState(false);
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [photoCounts, setPhotoCounts] = useState({});
    const [orderAmounts, setOrderAmounts] = useState({});
    const fetchedPhotosRef = useRef(new Set());
    const fetchedOrdersRef = useRef(new Set());

    useEffect(() => {
        if (!props.user?.id) props.getUser();
        props.getAllEvents();
        props.getEventSharedWithMe();
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (props.user?.role === "NULL" && props.user?.receiveNews === false) setShowNewsModal(true)
        // eslint-disable-next-line
    }, [props.user?.role]);

    useEffect(() => {
        if (!props.user?.id) props.getUser();
        // eslint-disable-next-line
    }, [props.user]);

    const ownRows = (props.events || []).map(event => ({
        id: event.id,
        name: event.name,
        code: event.code,
        dateFrom: event.dateFrom,
        active: event.active,
        hasFaces: !!event.hasFaces,
        completedDate: event.completedDate,
        publicGalleryActive: event.publicGalleryActive,
        shared: false,
    }));

    const acceptedSharedItems = (props.eventSharedWithMe || []).filter(item => item.status === "accepted");
    const sharedRows = acceptedSharedItems.map(item => ({
        id: item.revopicEvent?.id,
        name: item.revopicEvent?.name,
        code: item.revopicEvent?.code,
        dateFrom: item.revopicEvent?.dateFrom,
        active: undefined,
        shared: true,
    }));

    const allRows = [...ownRows, ...sharedRows];

    useEffect(() => {
        const ids = allRows.map(row => row.id).filter(Boolean);

        const idsToFetchPhotos = ids.filter(id => !fetchedPhotosRef.current.has(id));
        if (idsToFetchPhotos.length > 0) {
            idsToFetchPhotos.forEach(id => fetchedPhotosRef.current.add(id));
            Promise.allSettled(idsToFetchPhotos.map(id => StatsService.getNumberOfPhotos(id))).then(results => {
                setPhotoCounts(prev => {
                    const next = { ...prev };
                    results.forEach((result, index) => {
                        const id = idsToFetchPhotos[index];
                        next[id] = result.status === "fulfilled" ? (result.value?.data?.Data ?? null) : null;
                    });
                    return next;
                });
            });
        }

        // Los ingresos son del propietario: para un evento compartido no se piden (el backend
        // los deniega con 403 a quien no lo es) y la columna muestra «—».
        const ownIds = ownRows.map(row => row.id).filter(Boolean);
        const idsToFetchOrders = ownIds.filter(id => !fetchedOrdersRef.current.has(id));
        if (idsToFetchOrders.length > 0) {
            idsToFetchOrders.forEach(id => fetchedOrdersRef.current.add(id));
            Promise.allSettled(idsToFetchOrders.map(id => StatsService.getTotalAmountOrders(id))).then(results => {
                setOrderAmounts(prev => {
                    const next = { ...prev };
                    results.forEach((result, index) => {
                        const id = idsToFetchOrders[index];
                        next[id] = result.status === "fulfilled" ? (result.value?.data?.Data ?? null) : null;
                    });
                    return next;
                });
            });
        }
        // eslint-disable-next-line
    }, [props.events, props.eventSharedWithMe]);

    const onCancelNews = () => {
        const user = { ...props.user };
        user.receiveNews = false;
        user.role = "activeUser";
        props.postUser(user);
        setShowNewsModal(false);
    }

    const onConfirmNews = () => {
        const user = { ...props.user };
        user.receiveNews = true;
        user.role = "activeUser";
        props.postUser(user);
        setShowNewsModal(false);
    };

    const onAcceptSharedEvent = async (eventId) => {
        try {
            setShareLoading(true);

            const sharedWithMe = props.eventSharedWithMe.map(event =>
                event.revopicEvent?.id === eventId
                    ? { ...event, status: "accepted" }
                    : event
            );

            await props.editEventSharedWith(eventId, sharedWithMe);

            await Promise.all([
                props.getAllEvents(),
                props.getEventSharedWithMe()
            ]);
        } catch (error) {
            console.error(error);
        } finally {
            setShareLoading(false);
        }
    }

    const onDenySharedEvent = async () => {
        try {
            setShareLoading(true);

            const sharedWithMe = props.eventSharedWithMe.map(event =>
                event.revopicEvent?.id === eventIdToDeny
                    ? { ...event, status: "denied" }
                    : event
            );

            await props.editEventSharedWith(eventIdToDeny, sharedWithMe);

            await Promise.all([
                props.getAllEvents(),
                props.getEventSharedWithMe()
            ]);
        } catch (error) {
            console.error(error);
        } finally {
            setShareLoading(false);
            setShowDenyModal(false);
            setEventIdToDeny(undefined);
        }
    }

    const checkEventSharedWithIsPending = () => {
        if (props.eventSharedWithMe) {
            const pendingEvents = props.eventSharedWithMe.filter(event => event.status === "pending");
            return pendingEvents.length > 0;
        }
        return false;
    }

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }

    const compareByField = (aValue, bValue) => {
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    }

    const sortRows = (rows) => {
        if (!rows || rows.length === 0) return rows;

        return [...rows].sort((a, b) => {
            if (sortField === 'name') {
                return compareByField(a.name?.toLowerCase() || '', b.name?.toLowerCase() || '');
            }
            if (sortField === 'date') {
                return compareByField(new Date(a.dateFrom), new Date(b.dateFrom));
            }
            return 0;
        });
    }

    const sortSharedEvents = (events) => {
        if (!events || events.length === 0) return events;

        return [...events].sort((a, b) => {
            const eventA = a.revopicEvent;
            const eventB = b.revopicEvent;

            if (sortField === 'name') {
                return compareByField(eventA.name?.toLowerCase() || '', eventB.name?.toLowerCase() || '');
            }
            if (sortField === 'date') {
                return compareByField(new Date(eventA.dateFrom), new Date(eventB.dateFrom));
            }
            return 0;
        });
    }

    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <span className="material-icons rev-icon-sm rev-events-sort-header__icon rev-events-sort-header__icon--idle">unfold_more</span>;
        }
        return <span className="material-icons rev-icon-sm rev-events-sort-header__icon">
            {sortDirection === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
        </span>;
    }

    const locale = t('locale', { returnObjects: true }) || 'es-ES';
    // Los pedidos se cobran en la moneda del fotógrafo (user.currency); nunca «€» fijo.
    const currencyFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'currency', currency: props.user?.currency || 'EUR' }), [locale, props.user?.currency]);
    const formatCurrency = (value) => currencyFormatter.format(value);

    const now = new Date();
    const counts = {
        todos: allRows.length,
        proximos: allRows.filter(row => row.dateFrom && new Date(row.dateFrom) >= now).length,
        pasados: allRows.filter(row => row.dateFrom && new Date(row.dateFrom) < now).length,
        compartidos: allRows.filter(row => row.shared).length,
    };

    // Texto del buscador de la cabecera (?q= en la URL): nombre o código, acumulable al segmentado.
    const searchQuery = readEventsSearch(props.location);
    const filteredRows = sortRows(allRows).filter(row => {
        if (!matchesEventsSearch(searchQuery, row.name, row.code)) return false;
        if (filtroEstado === 'compartidos') return row.shared;
        if (filtroEstado === 'proximos') return row.dateFrom && new Date(row.dateFrom) >= now;
        if (filtroEstado === 'pasados') return row.dateFrom && new Date(row.dateFrom) < now;
        return true;
    });

    const photosLoaded = allRows.length === 0 || allRows.every(row => photoCounts[row.id] !== undefined);
    const totalPhotos = allRows.reduce((sum, row) => sum + (typeof photoCounts[row.id] === 'number' ? photoCounts[row.id] : 0), 0);

    const ordersLoaded = ownRows.length === 0 || ownRows.every(row => orderAmounts[row.id] !== undefined);
    const totalRevenue = ownRows.reduce((sum, row) => sum + (typeof orderAmounts[row.id] === 'number' ? orderAmounts[row.id] : 0), 0);

    // El saldo son CRÉDITOS, no dinero: mismo formato que el raíl (formatBalance).
    const balanceDisplay = formatBalance(props.user?.balance, locale);

    const photoCell = (row) => typeof photoCounts[row.id] === 'number' ? formatCount(photoCounts[row.id], locale) : '—';
    const revenueCell = (row) => !row.shared && typeof orderAmounts[row.id] === 'number' ? formatCurrency(orderAmounts[row.id]) : '—';

    const handleRowClick = (row) => {
        props.deselectEvent();
        if (row.shared) {
            props.history.push(`/events/${row.id}/shared-photo`);
        } else {
            props.history.push(`/events/${row.id}/basic`);
        }
    }

    // Selfies: activos (announce-complete), subida abierta con fotos, o sin fotos todavía.
    const downloadsState = (row) => {
        if (row.shared) return null;
        if (isEventPublished(row)) return { key: 'active', tone: 'ok' };
        if (photoCounts[row.id] === undefined && !row.hasFaces) return null;
        const hasPhotos = row.hasFaces || photoCounts[row.id] > 0;
        return hasPhotos ? { key: 'preparing', tone: 'warn' } : { key: 'none', tone: null };
    };

    const downloadsLabels = {
        active: t('eventsList.selfies.active'),
        preparing: t('eventsList.selfies.preparing'),
        none: t('eventsList.selfies.none'),
    };

    const renderDownloadsCell = (row) => {
        const state = downloadsState(row);
        if (!state) return <span className="rev-events-cell--num">—</span>;
        return <RevChip tone={state.tone || undefined}>{downloadsLabels[state.key]}</RevChip>;
    };

    // Publicado: «Publicar evento» (galería pública + listado de la app), independiente de los selfies.
    // Las filas compartidas no lo traen (el dueño la gobierna), así que van sin dato.
    const renderGalleryCell = (row) => {
        if (row.shared) return <span className="rev-events-cell--num">—</span>;
        const on = isPublicGalleryOn(row);
        return <RevChip tone={on ? 'ok' : undefined}>{t(on ? 'eventsList.published.on' : 'eventsList.published.off')}</RevChip>;
    };

    const filterItems = [
        { value: 'todos', label: t('eventsList.filters.all'), count: counts.todos },
        { value: 'proximos', label: t('eventsList.filters.upcoming'), count: counts.proximos },
        { value: 'pasados', label: t('eventsList.filters.past'), count: counts.pasados },
        { value: 'compartidos', label: t('eventsList.filters.shared'), count: counts.compartidos },
    ];

    const gridClassName = 'rev-events-grid';
    const isLoading = props.loading || props.loadingSharedWithMe;
    // Solo en la primera carga de cada llamada: en un refetch los datos ya estan en pantalla
    // y sustituirlos por el esqueleto seria un parpadeo (artboard L1820).
    const mostrarSkeleton = (props.loading && !props.eventsFetched) || (props.loadingSharedWithMe && !props.sharedFetched);
    const refrescando = isLoading && !mostrarSkeleton;

    return (
        <div className="rev-pagina">
            <RevPageHead
                title={t('events.title')}
                subtitle={t('eventsList.subtitle')}
                actions={
                    <RevButton as={Link} to="/events/new" variant="primary" onClick={() => props?.deselectEvent()}>
                        {t('events.newEventButton.text')}
                    </RevButton>
                }
            />

            {mostrarSkeleton ? <RevSkeleton.Page
                forma="lista"
                label={t('event.photos.loading')}
                metricas={4}
                sinDelta={1}
                columnas={7}
                filas={5}
                etiquetas={filterItems}
                contador={t('eventsList.count', { count: allRows.length })}
                clases={clasesEsqueleto(gridClassName)}
            /> : <div className="rev-events-metrics">
                <div className="rev-metric">
                    <span className="rev-metric__label">{t('eventsList.metrics.selfies')}</span>
                    <RevFigure>{formatCount(ownRows.filter(isEventPublished).length, locale)}</RevFigure>
                    <span className="rev-metric__delta">{t('eventsList.metrics.selfiesDelta', { upcoming: counts.proximos, past: counts.pasados })}</span>
                </div>
                <div className="rev-metric">
                    <span className="rev-metric__label">{t('eventsList.metrics.photos')}</span>
                    <RevFigure>{photosLoaded ? formatCount(totalPhotos, locale) : '—'}</RevFigure>
                    <span className="rev-metric__delta">{t('eventsList.metrics.photosDelta', { count: allRows.length })}</span>
                </div>
                <div className="rev-metric">
                    <span className="rev-metric__label">{t('eventsList.metrics.revenue')}</span>
                    <RevFigure>{ordersLoaded ? formatCurrency(totalRevenue) : '—'}</RevFigure>
                    <span className="rev-metric__delta rev-metric__delta--up">{t('eventsList.metrics.revenueDelta')}</span>
                </div>
                <div className="rev-metric">
                    <span className="rev-metric__label">{t('eventsList.metrics.balance')}</span>
                    <RevFigure>{balanceDisplay}</RevFigure>
                </div>
            </div>}

            {!mostrarSkeleton && <>
                <div className="rev-events-toolbar">
                    <RevSegmented role="tablist" items={filterItems} value={filtroEstado} onChange={setFiltroEstado} />
                    <span className="rev-events-toolbar__count">
                        {filtroEstado === 'todos' && !searchQuery
                            ? t('eventsList.count', { count: allRows.length })
                            : t('eventsList.countFiltered', { count: filteredRows.length, total: allRows.length })}
                    </span>
                </div>

                <div className={cx('rev-events-surface', refrescando && 'rev-refrescando')} aria-busy={refrescando || undefined}>
                    <div className={`${gridClassName} rev-events-grid__head`}>
                        <button type="button" className="rev-events-sort-header" onClick={() => handleSort('name')}>
                            <span>{t('eventsList.table.eventHeader')}</span>
                            {getSortIcon('name')}
                        </button>
                        <button type="button" className="rev-events-sort-header" onClick={() => handleSort('date')}>
                            <span>{t('eventsList.table.dateHeader')}</span>
                            {getSortIcon('date')}
                        </button>
                        <span className="rev-events-cell--right">{t('eventsList.table.photosHeader')}</span>
                        <span className="rev-events-cell--right">{t('eventsList.table.revenueHeader')}</span>
                        <span className="rev-events-cell--right">{t('eventsList.table.publishedHeader')}</span>
                        <span className="rev-events-cell--right">{t('eventsList.table.selfiesHeader')}</span>
                        <span></span>
                    </div>

                    {filteredRows.map((row) => (
                        <div key={row.id} className={`${gridClassName} rev-events-row`} onClick={() => handleRowClick(row)}>
                            <div className="rev-events-cell__event">
                                <Link
                                    to={row.shared ? `/events/${row.id}/shared-photo` : `/events/${row.id}/basic`}
                                    className="rev-events-cell__name"
                                    onClick={(e) => { e.stopPropagation(); props.deselectEvent(); }}
                                >
                                    {row.name}
                                </Link>
                                {row.shared && <span className="rev-events-cell__shared">{t('eventsList.table.sharedSubtitle')}</span>}
                                <span className="rev-mono rev-events-cell__code">{row.code}</span>
                            </div>
                            <span className="rev-num rev-events-cell--num">{formatShortDate(row.dateFrom, locale)}</span>
                            <span className="rev-num rev-events-cell--num rev-events-cell--right">{photoCell(row)}</span>
                            <span className="rev-num rev-events-cell--num rev-events-cell--right rev-events-cell--strong">{revenueCell(row)}</span>
                            <span className="rev-events-cell__status">{renderGalleryCell(row)}</span>
                            <span className="rev-events-cell__downloads">{renderDownloadsCell(row)}</span>
                            <span className="rev-events-cell__actions" aria-hidden="true">
                                <span className="material-icons rev-icon-sm">chevron_right</span>
                            </span>
                        </div>
                    ))}

                    {filteredRows.length === 0 && (searchQuery
                        ? <RevEmpty icon="search" title={t('eventsList.emptySearch.title', { q: searchQuery })} body={t('eventsList.emptySearch.body')} />
                        : <RevEmpty icon="event" title={t('eventsList.empty.title')} body={t('eventsList.empty.body')} />)}
                </div>
            </>}

            {!mostrarSkeleton && checkEventSharedWithIsPending() && <div className="rev-events-pending">
                <div className="rev-events-pending__header">
                    <h2 className="rev-events-pending__title">{t('Shared with me')}</h2>
                </div>
                {props?.eventSharedWithMe?.length > 0 && !props.loadingSharedWithMe && <div className="rev-events-surface">
                    <div className="rev-events-pending-grid rev-events-pending-grid__head">
                        <button type="button" className="rev-events-sort-header" onClick={() => handleSort('name')}>
                            <span>{t('events.table.nameHeader')}</span>
                            {getSortIcon('name')}
                        </button>
                        <button type="button" className="rev-events-sort-header" onClick={() => handleSort('date')}>
                            <span>{t('eventsList.pending.dateHeader')}</span>
                            {getSortIcon('date')}
                        </button>
                        <span>{t('eventsList.pending.codeHeader')}</span>
                        <span>{t('Status')}</span>
                        <span className="rev-events-cell--right">{t('events.table.actionsHeader')}</span>
                    </div>
                    {sortSharedEvents(props?.eventSharedWithMe || []).map((item, idx) => {
                        if (item.status !== "pending") return null;
                        const event = item.revopicEvent;
                        return (
                            <div key={idx} className="rev-events-pending-grid rev-events-pending-row">
                                <span className="rev-events-cell__name">{event.name}</span>
                                <span className="rev-num rev-events-cell--num">{formatShortDate(event.dateFrom, locale)}</span>
                                <span className="rev-mono">{event.code}</span>
                                <span><RevChip tone="warn">{t(item.status)}</RevChip></span>
                                <span className="rev-events-pending__actions">
                                    <RevButton size="sm" variant="secondary" disabled={shareLoading} onClick={() => onAcceptSharedEvent(event.id)}>
                                        <span className="material-icons rev-icon-sm">check</span>
                                        {t("Accept")}
                                    </RevButton>
                                    <RevButton
                                        size="sm" variant="danger"
                                        disabled={shareLoading}
                                        onClick={() => {
                                            setEventIdToDeny(event.id);
                                            setShowDenyModal(true);
                                        }}
                                    >
                                        <span className="material-icons rev-icon-sm">close</span>
                                        {t("Deny")}
                                    </RevButton>
                                </span>
                            </div>
                        );
                    })}
                </div>}
            </div>}

            {showDenyModal && <Modal
                title={t('This operation cannot be undone')}
                message={t('Are you sure you want to deny this event?')}
                showConfirmButton={true}
                showCancelButton={true}
                cancelButtonText={t('generic.alertButton.cancel')}
                confirmButtonText={t('Deny event')}
                onCancel={() => setShowDenyModal(false)}
                onConfirm={() => onDenySharedEvent()}
                isDanger={true}
                loading={props.loading}
            />}

            {showNewsModal === true &&
                <Modal
                    title={t('newsletter.title')}
                    message={t('newsletter.text')}
                    closable={false}
                    showConfirmButton={true}
                    showCancelButton={true}
                    cancelButtonText={t('newsletter.no')}
                    confirmButtonText={t('newsletter.yes')}
                    onCancel={onCancelNews}
                    onConfirm={onConfirmNews}
                    loading={props.loading}
                />
            }
        </div>
    );
};

const mapStateToProps = state => (
    {
        user: state.usersReducer.user,
        events: state.eventsReducer.events,
        eventSharedWithMe: state.eventsReducer.eventSharedWithMe,
        loading: state.eventsReducer.loading,
        loadingSharedWithMe: state.eventsReducer.loadingSharedWithMe,
        eventsFetched: state.eventsReducer.eventsFetched,
        sharedFetched: state.eventsReducer.sharedFetched
    }
);

const mapDispatchToProps =
{
    getAllEvents,
    getUser,
    postUser,
    deselectEvent,
    getEventSharedWithMe,
    editEventSharedWith
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EventsList));
